/**
 * Display helpers for scoping artifact type labels (bet type, assumption type).
 *
 * The capture pipeline emits type tokens as single PascalCase words
 * (`Desirability`, `ProblemFrequency`, `WillingnessToSpend`). Rendered raw they
 * read as one run-on word, so surface them split into spaced words.
 */

/**
 * Humanise a type token for display: split camelCase / PascalCase boundaries and
 * normalise snake/kebab separators to spaces.
 *   "ProblemFrequency"   → "Problem Frequency"
 *   "WillingnessToSpend" → "Willingness To Spend"
 *   "Desirability"       → "Desirability"   (single word — unchanged)
 *   "problem_frequency"  → "problem frequency"
 * Trims and collapses whitespace; returns '' for empty/whitespace input.
 */
export function humanizeType(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ') // snake_case / kebab-case separators → space
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camel/Pascal boundary → space
    .replace(/\s+/g, ' ')
    .trim();
}

// Re-exported from the shared widget text-format kit so the scoping cards and
// every other chat-thread card share one implementation.
export { capitalizeFirst } from '../../widgets/textFormat';

/**
 * Default accent for type pills (bet_type / assumption_type / goal_type), shared
 * across the transcript card and the rail card so a "type" reads consistently.
 * The scoping purple — deliberately OUTSIDE the green/amber/red risk
 * traffic-light ramp so a type never reads as a risk signal.
 */
export const TYPE_PILL_STYLE = { color: '#C084FC', background: 'rgba(124,58,237,0.12)' } as const;

export type ScRiskLevel = 'low' | 'medium' | 'high' | 'critical';

/**
 * Display metadata for a captured assumption `risk_level`. The colour ramps from
 * green (low) to red (critical) so the riskiest assumptions read at a glance.
 * Shared by the transcript card and the rail card so the chip is consistent.
 */
export const RISK_META: Record<ScRiskLevel, { label: string; color: string; bg: string }> = {
  low: { label: 'Low risk', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },
  medium: { label: 'Medium risk', color: '#EAB308', bg: 'rgba(234,179,8,0.12)' },
  high: { label: 'High risk', color: '#F97316', bg: 'rgba(249,115,22,0.12)' },
  critical: { label: 'Critical risk', color: '#EF4444', bg: 'rgba(239,68,68,0.14)' },
};

/** Severity rank for ordering — highest risk first; no risk sinks to the bottom. */
export const RISK_RANK: Record<ScRiskLevel, number> = { critical: 4, high: 3, medium: 2, low: 1 };

/** Stable comparator: order items by risk, most severe first (unranked last). */
export function byRiskDesc<T extends { risk?: ScRiskLevel }>(a: T, b: T): number {
  return (b.risk ? RISK_RANK[b.risk] : 0) - (a.risk ? RISK_RANK[a.risk] : 0);
}
